import { preventDefault } from '@/true-fsd/shared/utils/react'
import {
    Popover,
    PopoverContent,
    PopoverPortal,
    PopoverTrigger,
} from '@radix-ui/react-popover'
import { KktuResponse } from '@synapex/shared2/gateway-rest-api'
import cl from 'clsx'
import {
    ComponentPropsWithoutRef,
    forwardRef,
    memo,
    ReactNode,
    useState,
} from 'react'
import {
    useKktuQuery,
    UseSavedErid,
    UseUpdateVkOrdCreative,
} from '@/shared/api'
import { RemoveScroll } from 'react-remove-scroll'

import { CheckIcon } from '@/shared/icons'
import { KktuBadge } from './KktuBadbe'
import classes from './KktuSelect.module.scss'
import { type UseKktuSelectState } from './useSelectState'
import { filterSearch, fullItemText } from './utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { CreativeProps } from '../Creative'

export type KktuSelectProps = {
    disabled?: string | boolean
    error?: string | boolean
    popoverWidth: number
    mutate: UseUpdateVkOrdCreative['mutate']
} & UseKktuSelectState &
    Omit<ComponentPropsWithoutRef<'div'>, 'onSelect'> &
    Pick<
        CreativeProps,
        'draftContractId' | 'customContractId' | 'tableRowData'
    > &
    Pick<UseSavedErid, 'savedErid'>
export const KktuSelect = forwardRef<HTMLDivElement, KktuSelectProps>(
    (
        {
            state,
            disabled,
            error,
            popoverWidth,
            mutate,
            onOpenChange,
            onSelect,
            draftContractId,
            customContractId,
            tableRowData,
            savedErid,
            ...divProps
        },
        ref
    ) => {
        const { open, value } = state
        const { data = { order: [], record: {} } } = useKktuQuery()

        const [search, setSearch] = useState('')

        if (disabled) {
            const badge = (
                <KktuBadge
                    {...{ value, data, error: Boolean(error) }}
                    // className={'cursor-not-allowed'}
                />
            )

            let ret: ReactNode
            if (typeof disabled === 'string') {
                ret = (
                    <Tooltip>
                        <TooltipTrigger asChild>{badge}</TooltipTrigger>
                        <TooltipContent side="top">{disabled}</TooltipContent>
                    </Tooltip>
                )
            } else {
                ret = badge
            }

            return (
                <div
                    {...divProps}
                    ref={ref}
                    className={cl(classes.root, classes.disabled)}
                >
                    {ret}
                </div>
            )
        }

        const onChangeSearch: React.ChangeEventHandler<HTMLInputElement> = (
            evt
        ) => setSearch(evt.target.value)

        return (
            <div {...divProps} ref={ref} className={classes.root}>
                {open && (
                    <input
                        placeholder={fullItemText(data, value)}
                        autoFocus
                        value={search}
                        onChange={onChangeSearch}
                        className={classes.input}
                    />
                )}
                <ListPopover
                    {...{
                        mutate,
                        popoverWidth,
                        onOpenChange,
                        onSelect,
                        state,
                        search,
                        data,
                        draftContractId,
                        customContractId,
                        tableRowData,
                        savedErid,
                    }}
                    error={Boolean(error)}
                />
            </div>
        )
    }
)

type ListPopoverProps = {
    error: boolean
    popoverWidth: number
    data: KktuResponse
    search: string
    mutate: UseUpdateVkOrdCreative['mutate']
} & Pick<UseKktuSelectState, 'onSelect' | 'onOpenChange' | 'state'> &
    Pick<
        CreativeProps,
        'draftContractId' | 'customContractId' | 'tableRowData'
    > &
    Pick<UseSavedErid, 'savedErid'>
const ListPopover = ({
    error,
    popoverWidth,
    data,
    onSelect,
    onOpenChange,
    state: { open, value },
    search,
    mutate,
    draftContractId,
    customContractId,
    tableRowData,
    savedErid,
}: ListPopoverProps) => {
    const { activeCreative } = tableRowData

    return (
        <Popover
            open={open}
            onOpenChange={(value) => {
                onOpenChange(value)
            }}
        >
            <PopoverTrigger className={cl('grow', { ['h-0']: open })}>
                {!open && (
                    <KktuBadge
                        {...{ data, error, value }}
                        className={'hover:bg-gray-100'}
                    />
                )}
            </PopoverTrigger>

            <PopoverPortal>
                <PopoverContent
                    className={classes.popover}
                    align={'start'}
                    sideOffset={2}
                    avoidCollisions={false}
                    onOpenAutoFocus={preventDefault}
                    style={{ width: popoverWidth }}
                >
                    {open && (
                        <RemoveScroll className={classes.list}>
                            {filterSearch(data, search).map(
                                ({ code, name }) => {
                                    return (
                                        <ListItem
                                            {...{ code, name }}
                                            onSelect={(code) => {
                                                onSelect(code)
                                                if (
                                                    activeCreative &&
                                                    (draftContractId ||
                                                        customContractId) &&
                                                    savedErid
                                                ) {
                                                    mutate({
                                                        kktu: code,
                                                        creativeId:
                                                            activeCreative.id,
                                                        erid: savedErid,
                                                        draftContractId,
                                                        customContractId,
                                                    })
                                                }
                                            }}
                                            selected={value === code}
                                            key={code}
                                        />
                                    )
                                }
                            )}
                        </RemoveScroll>
                    )}
                </PopoverContent>
            </PopoverPortal>
        </Popover>
    )
}

type ListItemProps = {
    code: string
    name: string
    selected: boolean
} & Pick<UseKktuSelectState, 'onSelect'>
const ListItem = memo(({ code, name, selected, onSelect }: ListItemProps) => {
    const onClick = () => onSelect(code)

    return (
        <div
            {...{ onClick }}
            className={cl(classes.item, { [classes.selected]: selected })}
        >
            <div className={classes.code}>{code}</div>
            <div className={cl('truncate', classes.name)}>{name}</div>
            <div className={'grow'} />
            {selected && <CheckIcon className={classes.icon} />}
        </div>
    )
})
